import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import { listProducts, getProduct } from "../../api/products";
import { listProductTypes } from "../../api/productTypes";
import ProductDetail from "./ProductDetail";

export default function ProductsAdmin() {
  const [productTypes, setProductTypes] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [productTypeId, setProductTypeId] = useState("");
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [detailFor, setDetailFor] = useState(null); // null | { id?, fresh? } | "new"

  useEffect(() => {
    listProductTypes()
      .then((d) => setProductTypes(Array.isArray(d) ? d : []))
      .catch((e) => setError(e.message || "Failed to load product types"));
  }, []);

  const fetchAll = useCallback(() => {
    setLoading(true);
    setError("");
    listProducts({
      productTypeId: productTypeId || undefined,
      q: search.trim() || undefined,
      activeOnly,
    })
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch((e) => setError(e.message || "Failed to load products"))
      .finally(() => setLoading(false));
  }, [productTypeId, search, activeOnly]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const typeNameById = useMemo(() => {
    const m = {};
    productTypes.forEach((t) => { m[t.id] = t.name; });
    return m;
  }, [productTypes]);

  if (detailFor === "new") {
    return (
      <ProductDetail
        productId={null}
        productTypes={productTypes}
        onClose={() => { setDetailFor(null); fetchAll(); }}
        onCreated={(id) => setDetailFor({ id })}
      />
    );
  }
  if (detailFor && detailFor.id) {
    return (
      <ProductDetail
        productId={detailFor.id}
        productTypes={productTypes}
        onClose={() => { setDetailFor(null); fetchAll(); }}
      />
    );
  }

  const openDetail = async (id) => {
    try {
      // pre-warm so ProductDetail doesn't re-fetch
      await getProduct(id);
      setDetailFor({ id });
    } catch (e) {
      setError(e.message || "Failed to load product");
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Products & Pricing</Typography>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Type</InputLabel>
          <Select
            label="Type"
            value={productTypeId}
            onChange={(e) => setProductTypeId(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {productTypes.map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small" label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FormControlLabel
          control={<Checkbox checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />}
          label="Active only"
        />
        <Button startIcon={<RefreshIcon />} onClick={fetchAll}>Refresh</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDetailFor("new")}>New Product</Button>
      </Stack>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Derivation</TableCell>
              <TableCell>Default Markup %</TableCell>
              <TableCell>Active</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center">No products</TableCell></TableRow>
            ) : rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => openDetail(row.id)}
              >
                <TableCell>{row.name}</TableCell>
                <TableCell>{typeNameById[row.productTypeId] || row.productTypeId}</TableCell>
                <TableCell>{row.derivation}</TableCell>
                <TableCell>{row.defaultMarkupPct ?? ""}</TableCell>
                <TableCell>{row.isActive ? "Yes" : "No"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
